<template>
  <section>
    <!-- Page content-->
    <div class="content-wrapper">
      <div class="content-heading">
         Upload Documentation
         <small data-localize="dashboard.WELCOME"></small>
      </div>

      <div class="row">
        <spinner v-if="loading"></spinner>
         <div class="col-lg-12">
            <!-- START widget-->
            <onboarding-wizard :userStatus="userStatus"></onboarding-wizard>
            <div class="panel panel-default">

                <div v-if="!uploadSucceeded && needsDocumentation && documentationReason">
                    <div role="alert" class="alert alert-danger">
                      <h4><em class="fa fa-warning pr-sm"></em>Verification of your documents failed.</h4>
                      <p>{{ getDocumentationReason() }}</p>
                    </div>
                </div>
                <div v-if="uploadFailed" role="alert" class="alert alert-danger">
                  <h4><em class="fa fa-warning pr-sm"></em>There was a problem uploading your documentation. Please try again.</h4>
                  <p>Make sure that your documentation is in a valid format and is no more than 10 MB.</p>
                </div>
                <div v-if="uploadSucceeded">
                    <div role="alert" class="alert alert-info">
                      <h4><em class="icon-info pr-sm"></em>We've received your documentation</h4>
                      <p>Please allow up to 48 hours while we verify it.</p>
                    </div>
                </div>
                <div v-if="documentationInReview">
                    <div role="alert" class="alert alert-info">
                      <h4><em class="icon-info pr-sm"></em>Your documentation is being verified.</h4>
                      <p>Please allow up to 48 hours while we verify it.</p>
                    </div>
                </div>

               <div class="panel-heading">
                  <h4 class="panel-title">Additional documentation required</h4>
               </div>
              <div class="panel-body">
                   <p class="mt-lg">In order to complete verifying your personal information, we need you to upload a copy of one of the following documents:</p>
                  <form>
                     <div class="radio c-radio">
                        <label>
                           <input type="radio" name="documentType" v-model="documentType" value="license">
                           <span class="fa fa-circle"></span>Driver's license</label>
                     </div>
                     <div class="radio c-radio">
                        <label>
                           <input type="radio" name="documentType" v-model="documentType" value="passport">
                           <span class="fa fa-circle"></span>Passport</label>
                     </div>
                     <div class="radio c-radio">
                        <label>
                           <input type="radio" name="documentType" v-model="documentType" value="idCard">
                           <span class="fa fa-circle"></span>ID card</label>
                     </div>
                     <div class="radio c-radio">
                        <label>
                           <input type="radio" name="documentType" v-model="documentType" value="other">
                           <span class="fa fa-circle"></span>Other</label>
                     </div>
                  </form>
                  <div v-if="selectedFile" class="row">
                      <div class="col-md-4">
                        <div data-filter-group="image" class="panel discoverer">
                           <div class="panel-body text-center">
                              <div class="clearfix discover">
                                 <div class="pull-right">
                                    <a href="#" title="Download" class="text-muted mr-sm">
                                       <em class="fa fa-download fa-fw"></em>
                                    </a>
                                 </div>
                              </div>
                              <a href="#" class="file-icon ph-lg">
                                 <em class="fa fa-5x fa-file-image-o text-green"></em>
                              </a>
                              <p>
                                 <small class="text-dark">{{selectedFile.name}}</small>
                              </p>
                              <div class="clearfix m0 text-muted">
                                 <small class="pull-right">{{(selectedFile.size/1024).toFixed(2).toString() + "KB"}}</small>
                              </div>
                           </div>
                        </div>
                     </div>
                  </div>
                  <form role="form" class="form-inline">
                     <div class="form-group mt-lg" :class="{ 'has-error': isFileInvalid }">
                        <input
                          @change="fileChanged" 
                          type="file" 
                          data-classbutton="btn btn-default"
                          data-classinput="form-control inline"
                          class="form-control filestyle" 
                          id="filestyle-0" 
                          tabindex="-1" 
                          style="position: absolute; clip: rect(0px 0px 0px 0px);"
                          :accept="getAcceptedFileExtensions()"
                        >
                        <div class="bootstrap-filestyle input-group">
                          <input type="text" class="form-control " placeholder="" disabled="" :value="selectedFile ? selectedFile.name : ''">
                          <span class="group-span-filestyle input-group-btn" tabindex="0">
                            <label
                              for="filestyle-0"
                              class="btn btn-default"
                            >
                              <span class="icon-span-filestyle fa fa-folder-open"></span>
                              <span class="buttonText">Choose file</span>
                            </label>
                          </span>
                        </div>
                        <span v-if="isFileInvalid" class="help-block m-b-none">The file you selected is not valid. Please select a different file.</span>
                        <span class="help-block">Your file must be in {{ supportedFileExtensions }} format and cannot exceed 10MB.</span>
                    </div>
                    <ul class="panel-list mt-lg mb-xl text-muted">
                        <li class="help-block">Ensure that your document has not expired.</li>
                        <li class="help-block">The document should not look fuzzy or cropped.</li>
                        <li class="help-block">Make sure the name and address match your investor profile information.</li>
                    </ul>
                  </form>
                  <button type="submit" class="btn btn-default btn-lg" :disabled="!canSubmit()" @click="handleUpload">Submit</button>
               </div>
            </div>
            <!-- END widget-->
         </div>
      </div> <!-- row -->
    </div>
  </section>
</template>
<script>
  import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
  import documentRejectionMessages from '@/data/static/documentRejectionMessages';
  import {
    SET_ACCOUNT_STATUS,
    SET_NEEDS_DOCUMENTATION,
    SET_DOCUMENTATION_REASON,
  } from '@/store/mutation-types';

  import Spinner from '@/components/widgets/Spinner';
  import OnboardingWizard from '@/components/OnboardingWizard';
  import { formatFileExtensions } from '@/util';

  const personalAcceptedFileExtensions = [
    'png',
    'jpg',
    'jpeg',
  ];

  const businesAcceptedFileExtensions = [
    'png',
    'jpg',
    'jpeg',
    'pdf',
  ];

  const MAX_FILE_SIZE = 10485760;

  export default {
    name: 'documentUpload',
    data() {
      return {
        documentType: 'other',
        isFileInvalid: false,
        uploadFailed: false,
        uploadSucceeded: false,
        selectedFile: null,
        loading: false,
        acceptedFileExtensions: businesAcceptedFileExtensions,
      };
    },
    created() {
      this.checkIfUserNeedsDocumentation();
      this.getUserProfile()
        .then((user) => {
          if (user.userType === 'INVESTOR') {
            this.acceptedFileExtensions = personalAcceptedFileExtensions;
          }
          const { status } = user.accounts[0];
          this.setAccountStatus(status);
        });
    },
    beforeMount() {
      this.checkUserStatus();
    },
    computed: {
      ...mapState('app', [
        'activeAccount',
        'user',
      ]),
      ...mapState('documentUpload', [
        'documentIsUploading',
        'needsDocumentation',
        'documentationReason',
      ]),
      ...mapState('home', [
        'userStatus',
      ]),
      ...mapGetters('app', [
        'hasBeenCreatedAccount',
      ]),
      supportedFileExtensions() {
        return formatFileExtensions(this.acceptedFileExtensions);
      },
      documentationInReview() {
        return !this.needsDocumentation && this.documentationReason === 'DocumentPending';
      },
    },
    methods: {
      ...mapActions('home', [
        'getUserStatus',
      ]),
      ...mapActions('accountCreation', [
        'getUserProfile',
      ]),
      ...mapActions('documentUpload', [
        'uploadDocument',
        'checkIfUserNeedsDocumentation',
      ]),
      ...mapMutations('app', {
        setAccountStatus: SET_ACCOUNT_STATUS,
      }),
      ...mapMutations('documentUpload', {
        setDocumentionNeeds: SET_NEEDS_DOCUMENTATION,
        setDocumentationReason: SET_DOCUMENTATION_REASON,
      }),
      canSubmit() {
        return !!this.needsDocumentation && !this.documentIsUploading
          && !!this.documentType && !!this.selectedFile && !this.uploadSucceeded;
      },
      getAcceptedFileExtensions() {
        return this.acceptedFileExtensions.map(ext => `.${ext}`).join(',');
      },
      fileChanged({ target: { files } }) {
        this.isFileInvalid = false;
        if (files.length) {
          const selectedFile = files[0];
          const extension = selectedFile.name.split('.').pop().toLowerCase();
          const { size } = selectedFile;
          if (this.acceptedFileExtensions.indexOf(extension) === -1 || size > MAX_FILE_SIZE) {
            this.isFileInvalid = true;
            this.selectedFile = null;
          } else {
            this.selectedFile = selectedFile;
          }
        }
      },
      resetState() {
        this.isFileInvalid = false;
        this.uploadFailed = false;
        this.uploadSucceeded = false;
      },
      async handleUpload() {
        this.resetState();
        this.loading = true;
        return this.uploadDocument({
          file: this.selectedFile,
          data: {
            documentType: this.documentType,
          },
        })
        .then(async () => {
          this.loading = false;
          this.uploadSucceeded = true;
          await this.checkUserStatus();
        })
        .catch(() => {
          this.loading = false;
          this.uploadFailed = true;
        });
      },
      getDocumentationReason() {
        const reason = documentRejectionMessages[this.documentationReason];
        return reason || documentRejectionMessages.default;
      },
      async checkUserStatus() {
        this.loading = true;
        if (!this.hasBeenCreatedAccount) {
          return this.$router.push({ name: 'AccountSummary' });
        }
        return this.getUserStatus({
          accountId: this.activeAccount.id,
        }).then(({ status, needs }) => {
          if (status === 'suspended') {
            this.$router.push({ name: 'AccountSuspended' });
          } else if (status === 'document' || status === 'document_migrated_user') {
            this.loading = false;
          } else if (needs.includes('FUNDING_SOURCE')) {
            this.$router.push({ name: 'AddFundingSource' });
          } else if (needs.includes('NOTE')) {
            this.$router.push({ name: 'PurchaseNote', query: { first: 1 } });
          } else if (!needs.length) {
            this.$router.push({ name: 'AccountSummary' });
          }
        }).catch(() => {
          this.loading = false;
        });
      },
    },
    components: {
      spinner: Spinner,
      'onboarding-wizard': OnboardingWizard,
    },
  };
</script>
<style scoped>
.text-green {
  color: #3c763d;
}
.panel-list {
  margin-bottom: 20px;
  padding-left: 18px;
}
.panel-list li.help-block {
  display: list-item;
  margin: 6px 0;
  padding-left: 6px;
  margin-left: 0;
}
.line-separator {
  padding: 8px;
  line-height: 1.52857143;
  vertical-align: top;
  border-top: 1px solid #eee;
}
.panel-heading {
  padding: 15px;
}
.panel-title {
  font-size: 20px;
}
.panel-subtitle {
  font-size: 16px;
}
label.btn-default {
  height: 34px;
  padding-top: 9px;
}
.radio {
  margin: 12px 0;
}
.radio .fa {
  margin-right: 12px;
}

</style>



// WEBPACK FOOTER //
// DocumentUpload.vue?2c6df97e